import { createSlice, PayloadAction, createAsyncThunk } from '@reduxjs/toolkit';
import { LoanApplication } from '../types';
import type { RootState } from '.';
import { calculateCreditScore, calculateAdvancedCreditScore } from '../services/creditScoringService';
import { logAction } from './auditLogSlice';
import axios from 'axios';

const backendUrl = 'http://localhost:3001/api';

interface ApplicationState {
  applications: LoanApplication[];
  loading: boolean;
  error: string | null;
  pagination: {
    currentPage: number;
    totalPages: number;
    totalRecords: number;
    recordsPerPage: number;
    hasNextPage: boolean;
    hasPreviousPage: boolean;
  } | null;
}

const initialState: ApplicationState = {
  applications: [],
  loading: false,
  error: null,
  pagination: null,
};

export const fetchApplications = createAsyncThunk('applications/fetchApplications', async () => {
    const response = await axios.get(`${backendUrl}/applications`);
    return response.data;
});

export const fetchApplicationsWithPagination = createAsyncThunk(
  'applications/fetchApplicationsWithPagination', 
  async (params: { page?: number; limit?: number; merchantId?: string; start?: string; end?: string }) => {
    const response = await axios.get(`${backendUrl}/applications`, { params });
    return response.data;
  }
);

export const submitApplication = createAsyncThunk(
    'applications/submitApplication',
    async (application: LoanApplication, { dispatch, getState }) => {
        const { config, auth } = getState() as RootState;
        
        // Use advanced scoring if enabled, otherwise fall back to traditional
        let scoringResult;
        if (config.fraudDetectionEnabled || config.mlScoringEnabled) {
            scoringResult = await calculateAdvancedCreditScore(application, config);
        } else {
            scoringResult = calculateCreditScore(application, config);
        }
        
        const scoredApplication: LoanApplication = {
            ...application,
            merchantId: auth.user?.role === 'merchant' ? auth.user?.merchantId : application.merchantId,
            creditScore: scoringResult.score,
            status: scoringResult.status,
            interestRate: scoringResult.interestRate,
        };
        
        const response = await axios.post(`${backendUrl}/applications`, scoredApplication);

        dispatch(logAction({
            user: auth.user?.email || 'System',
            action: 'New Loan Application Submitted',
            details: {
                applicationId: scoredApplication.id,
                applicantName: scoredApplication.applicantName,
                status: scoredApplication.status,
                score: scoredApplication.creditScore,
                scoringModel: 'advanced' in scoringResult ? 'advanced' : 'traditional',
            }
        }));
        
        return response.data;
    }
);

export const updateApplicationStatus = createAsyncThunk(
    'applications/updateApplicationStatus',
    async ({ id, status }: { id: string; status: 'approved' | 'rejected' }, { dispatch }) => {
        await axios.put(`${backendUrl}/applications/${id}`, { status });
        dispatch(logAction({
            user: 'System', // Or get user from state
            action: `Application ${status}`,
            details: { applicationId: id }
        }));
        return { id, status };
    }
);

const applicationSlice = createSlice({
  name: 'applications',
  initialState,
  reducers: {
    // Reducers can be added here if needed for synchronous actions
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchApplications.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchApplications.fulfilled, (state, action: PayloadAction<any>) => {
        // Handle both old array format and new paginated format
        if (action.payload.data) {
          // New paginated format
          state.applications = action.payload.data;
          state.pagination = action.payload.pagination;
        } else {
          // Old array format (fallback)
          state.applications = action.payload;
          state.pagination = null;
        }
        state.loading = false;
      })
      .addCase(fetchApplications.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch applications';
      })
      .addCase(fetchApplicationsWithPagination.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchApplicationsWithPagination.fulfilled, (state, action: PayloadAction<any>) => {
        // Handle both old array format and new paginated format
        if (action.payload.data) {
          // New paginated format
          state.applications = action.payload.data;
          state.pagination = action.payload.pagination;
        } else {
          // Old array format (fallback)
          state.applications = action.payload;
          state.pagination = null;
        }
        state.loading = false;
      })
      .addCase(fetchApplicationsWithPagination.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch applications';
      })
      .addCase(submitApplication.fulfilled, (state, action: PayloadAction<LoanApplication>) => {
        state.applications.push(action.payload);
      })
      .addCase(updateApplicationStatus.fulfilled, (state, action) => {
        const index = state.applications.findIndex(app => app.id === action.payload.id);
        if (index !== -1) {
          state.applications[index].status = action.payload.status;
        }
      });
  },
});

export default applicationSlice.reducer;

// Selectors
export const selectApplications = (state: RootState) => state.applications.applications;
export const selectApplicationsPagination = (state: RootState) => state.applications.pagination;
export const selectApplicationsLoading = (state: RootState) => state.applications.loading;
export const selectApplicationsError = (state: RootState) => state.applications.error; 